import React from "react";
import {motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import {formatByType} from "./formatters";
import DeltaBadge from "../components/DeltaBadge";

/**
 * A single KPI metric card. Handles its own loading skeleton and empty state.
 * Animates in/out via framer-motion (driven by <AnimatePresence> in the grid).
 */
const KpiCard = ({definition, data, isLoading, isDarkMode, compareData}) => {
    const {t} = useTranslation("activite");
    const {labelKey, accessor, format, icon: Icon, accent = "#4DB6AC", invertTrend = false} = definition;
    const label = t(labelKey);

    const rawValue = data ? accessor(data) : undefined;
    const hasValue = rawValue !== undefined && rawValue !== null && rawValue !== "";
    const display = hasValue ? formatByType(rawValue, format) : "—";
    // Text KPIs (e.g. peak hour label) have no meaningful % change.
    const showDelta = compareData && hasValue && format !== "text";

    return (
        <motion.div
            layout
            initial={{opacity: 0, scale: 0.95, y: 8}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.95, y: -8}}
            transition={{duration: 0.2}}
            className={`rounded-xl border p-4 shadow-sm ${
                isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3
                        className={`truncate text-xs font-medium uppercase tracking-wide ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                        title={label}
                    >
                        {label}
                    </h3>
                    {isLoading ? (
                        <div className="mt-2 h-6 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                    ) : (
                        <p
                            className={`mt-1.5 truncate text-xl font-bold ${
                                hasValue
                                    ? isDarkMode
                                        ? "text-gray-100"
                                        : "text-gray-800"
                                    : isDarkMode
                                        ? "text-gray-600"
                                        : "text-gray-400"
                            }`}
                            title={display}
                        >
                            {display}
                        </p>
                    )}
                    {!isLoading && showDelta && (
                        <DeltaBadge
                            current={rawValue}
                            previous={accessor(compareData) ?? 0}
                            formatValue={(v) => formatByType(v, format)}
                            invert={invertTrend}
                            isDarkMode={isDarkMode}
                            align="start"
                        />
                    )}
                </div>
                {Icon && (
                    <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                        style={{backgroundColor: `${accent}1A`, color: accent}}
                    >
                        <Icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default KpiCard;
